import React, { useState } from "react";

import { useRecoilValueLoadable, useSetRecoilState } from "recoil";
import {
  asyncCardData,
  asyncGetSubscriptionState,
  cardType,
  deleteSubscription,
  orderReceipt,
  refund,
  removeCard,
  updateCardInfo,
} from "lib/store/billing";
import {
  customToastState,
  errorToastState,
  modalState,
} from "lib/store/common";
import { asyncGetPlan, updatePlan } from "lib/store/user";

import { Button, InlineBox } from "@atsent/components";

import { CurrentPlan } from "../CurrentPlan";

import "./Billing.scss";

export type BillingProps = {
  cx?: string;
};

const usePaymentInfo = () => {
  const payment = useRecoilValueLoadable(asyncCardData);
  const plan = useRecoilValueLoadable(asyncGetPlan);
  const sub = useRecoilValueLoadable(asyncGetSubscriptionState);

  const returnVal: {
    state: any;
    isSubscription: boolean;
    appliedCardInfo?: cardType;
    plan: string;
  } = {
    state: payment.state,
    isSubscription: false,
    appliedCardInfo: undefined,
    plan: "ENTERPRISE",
  };

  if (payment.state === "hasValue" && !!payment.contents) {
    returnVal.appliedCardInfo = payment.contents;
  }

  if (sub.state === "hasValue") {
    returnVal.isSubscription = sub.contents;
  }

  if (sub.state === "hasValue") {
    returnVal.plan = plan.contents.type;
  }

  return returnVal;
};

export const Billing: React.FC<BillingProps> = ({ cx = "" }) => {
  const [cancelSubOpen, setCancelSubOpen] = useState(false);
  const [reason, setReason] = useState("");
  const setErrorToast = useSetRecoilState(errorToastState);
  const setToast = useSetRecoilState(customToastState);
  const refreshCardData = useSetRecoilState(updateCardInfo);
  const setModal = useSetRecoilState(modalState);

  const refresh = useSetRecoilState(updatePlan);

  const { appliedCardInfo, isSubscription, plan } = usePaymentInfo();

  const handleCancelSubscription = async () => {
    try {
      await deleteSubscription({ reason });
      await refreshCardData(new Date());
      await setReason("");
      await setCancelSubOpen(false);
    } catch (e) {
      setErrorToast(e);
    }
  };

  const handleRefund = async () => {
    try {
      const { imp_uid } = (await orderReceipt({ size: 1, status: "paid" }))
        .results[0].payment_data;
      await refund({ imp_uid, reason });
      await refreshCardData(new Date());
      await setReason("");
      await refresh(new Date());
      await setCancelSubOpen(false);
    } catch (e) {
      setErrorToast(e);
    }
  };

  return (
    <div className={`Billing ${cx}`}>
      <CurrentPlan />

      <InlineBox title={"Payment information"} cx={"payment-info"}>
        <div className="body">
          <div className="l">
            <span>Payment method</span>
            <br />
            <br />
            <span className={"text"}>
              {!!appliedCardInfo ? (
                <>
                  {appliedCardInfo?.card_data.card_name}
                  {appliedCardInfo?.card_data.card_number}
                </>
              ) : (
                <>등록된 카드가 없습니다.</>
              )}
            </span>
          </div>
          <div className="button-wrapper">
            {!!appliedCardInfo ? (
              <Button
                click={async () => {
                  await removeCard();
                  await refreshCardData(new Date());
                  await setToast({
                    type: "success",
                    text: "카드 삭제가 완료되었습니다...",
                  });
                }}
                size={"small"}
                outline
              >
                Delete
              </Button>
            ) : (
              <Button
                size={"small"}
                click={() =>
                  setModal((_) => ({ ..._, myAccount: false, addCard: true }))
                }
                outline
              >
                등록
              </Button>
            )}
          </div>
        </div>
      </InlineBox>
      <br/>

      {plan !== "FREE" && (
        <>
          {isSubscription ? (
            <>
              {cancelSubOpen ? (
                <>
                  <span
                    className="underline"
                    onClick={() => setCancelSubOpen(false)}
                  >
                    취소
                  </span>
                  <input
                    className={"cancel-reason"}
                    type="text"
                    placeholder={"해지 사유 입력"}
                    onChange={(e) => setReason(e.target.value)}
                  />
                  <span
                    className="underline"
                    onClick={() => handleCancelSubscription()}
                  >
                    확인
                  </span>
                </>
              ) : (
                <span
                  className="underline"
                  onClick={() => setCancelSubOpen(true)}
                >
                  구독 취소
                </span>
              )}
            </>
          ) : (
            <>
              {cancelSubOpen ? (
                <>
                  <span
                    className="underline"
                    onClick={() => setCancelSubOpen(false)}
                  >
                    취소
                  </span>
                  <input
                    className={"cancel-reason"}
                    type="text"
                    placeholder={"환불 사유 입력"}
                    onChange={(e) => setReason(e.target.value)}
                  />
                  <span className="underline" onClick={() => handleRefund()}>
                    확인
                  </span>
                </>
              ) : (
                <span
                  className="underline"
                  onClick={() => setCancelSubOpen(true)}
                >
                  환불
                </span>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};
